import { useRouter } from 'next/router';
import { ComponentProps, useCallback, useEffect, useRef, useState } from 'react';
import InterceptModal from '@bridebook/web/components/bbcommon/intercept-modal/intercept-modal-component';
import { useInterceptModal } from '@bridebook/web/lib/ui/modal/use-intercept-modal';

interface IConfirmExitModalProps
  extends Pick<
    ComponentProps<typeof InterceptModal>,
    'text' | 'title' | 'primaryButtonText' | 'secondaryButtonText'
  > {
  onLeaveWithoutSaving?: () => void;
  isDirty: boolean;
  id: string;
  checkIfPathAllowed?: (url: string) => boolean;
  onHandleRouteChangeStart?: (url: string) => void;
}

interface IArgs extends IConfirmExitModalProps {}

export const useConfirmExitModal = ({
  onLeaveWithoutSaving,
  isDirty,
  id,
  checkIfPathAllowed,
  onHandleRouteChangeStart,
  ...interceptModalProps
}: IArgs) => {
  const { openModal: _openModal } = useInterceptModal();
  const router = useRouter();
  const [allowedNavigation, setAllowedNavigation] = useState(false);
  const targetNavigation = useRef<string | null>(null);

  const continueNavigation = useCallback(() => {
    setAllowedNavigation(true);
    if (targetNavigation.current) {
      router.replace(targetNavigation.current); // Prevents partial state changes
      targetNavigation.current = null;
    }
    if (onLeaveWithoutSaving) onLeaveWithoutSaving();
  }, [onLeaveWithoutSaving, router]);

  const abandonNavigation = useCallback(() => {
    targetNavigation.current = null;
  }, []);

  const openModal = useCallback(() => {
    _openModal(id, {
      ...interceptModalProps,
      onPrimaryButtonClick: abandonNavigation,
      onSecondaryButtonClick: continueNavigation,
    });
  }, [_openModal, abandonNavigation, continueNavigation, id, interceptModalProps]);

  // Intercept route change
  useEffect(() => {
    const handleRouteChangeStart = (url: string) => {
      if (!isDirty || allowedNavigation) return; // Allow navigation if form isn't dirty
      if (checkIfPathAllowed?.(url)) return;
      onHandleRouteChangeStart?.(url);

      targetNavigation.current = url;
      openModal();
      router.events.emit('routeChangeError');
      throw 'Navigation aborted'; // Prevents navigation
    };

    router.events.on('routeChangeStart', handleRouteChangeStart);

    return () => {
      router.events.off('routeChangeStart', handleRouteChangeStart);
    };
  }, [isDirty, openModal, router, allowedNavigation, checkIfPathAllowed, onHandleRouteChangeStart]);

  // Intercept browser unload
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = ''; // Required for modern browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, openModal]);

  return null;
};
